import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo } from 'react'

import { useIndividual } from '@/api/hooks/useIndividual'
import { useMe } from '@/api/hooks/useMe'
import { selectIndividualAction } from '@/redux/actions'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'

export type IndividualType = NonNullable<ReturnType<typeof useIndividual>['data']>

export const useIndividuals = () => {
  const selectedIndividualId = useAppSelector(state => state.settings.selectedIndividualId)
  const rehydratedSettings = useAppSelector(state => state.settings._persist.rehydrated)
  const appDispatch = useAppDispatch()
  const navigate = useNavigate()
  const { data: user } = useMe()
  const { data: individual, ...individualQuery } = useIndividual(selectedIndividualId)

  const individuals = useMemo(() => user?.individuals ?? [], [user?.individuals])
  const selectedIndividual = useMemo(
    () => individuals.find(individual => individual.id === selectedIndividualId),
    [individuals, selectedIndividualId],
  )

  const selectIndividual = useCallback(
    (id: string) => {
      // Re-selecting the current individual shouldn't yank the user out of the
      // page they're on.
      if (id === selectedIndividualId) return

      appDispatch(selectIndividualAction(id))

      // Detail routes are scoped to the individual that was selected when they were
      // opened — /notes/$noteId, /errands/$id and the documents routes all carry ids
      // that don't belong to the new individual. Reset to home instead of leaving a
      // dangling route behind. `replace` because "back" to a route belonging to the
      // previous individual isn't a state worth returning to.
      navigate({ replace: true, to: '/home' })
    },
    [appDispatch, navigate, selectedIndividualId],
  )

  useEffect(() => {
    if (!rehydratedSettings || selectedIndividual || individualQuery.isLoading) return
    const [firstIndividual] = individuals

    if (firstIndividual) {
      appDispatch(selectIndividualAction(firstIndividual.id))
    } else {
      appDispatch(selectIndividualAction(undefined))
    }
  }, [selectedIndividual, individuals, rehydratedSettings])

  return {
    individualQuery: { ...individualQuery, data: individual },
    individuals,
    selectedIndividual: individual,
    selectedIndividualId,
    selectIndividual,
  }
}
